import React, { useState } from "react";
import {
  View,
  Modal,
  ScrollView,
  SafeAreaView,
  TouchableWithoutFeedback,
  StyleSheet,
  Platform,
  useWindowDimensions,
} from "react-native";
import { CommentsSection } from "./CommentsSection";
import type { CommentsApi, CommentTargetKind } from "./CommentsSection";
import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../../../components/common/general/CustomButton/CustomButton";
import { ChatCircleIcon } from "@phosphor-icons/react";

interface CommentPinProps {
  targetKind: CommentTargetKind;
  targetId: string;
  api: CommentsApi;
  contextLabel?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  style?: object;
}

// ─── CommentPin ────────────────────────────────────────────────────────────

export function CommentPin({
  targetKind,
  targetId,
  api,
  contextLabel,
  open: controlledOpen,
  onOpenChange,
  disabled,
  style,
}: CommentPinProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const { width: screenWidth } = useWindowDimensions();

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };

  const counts = api.countsFor(targetKind, targetId);
  const hasUnresolved = counts.unresolved > 0;
  const hasResolvedOnly = !hasUnresolved && counts.total > 0;

  const triggerStyle = hasUnresolved
    ? styles.triggerUnresolved
    : hasResolvedOnly
      ? styles.triggerResolved
      : styles.triggerEmpty;

  const triggerTextStyle = hasUnresolved
    ? styles.triggerTextUnresolved
    : hasResolvedOnly
      ? styles.triggerTextResolved
      : styles.triggerTextEmpty;

  const popoverWidth = Math.min(420, screenWidth - 32);

  return (
    <View style={style}>
      <CustomButton
        text={hasUnresolved ? String(counts.unresolved) : hasResolvedOnly ? String(counts.total) : "Add a comment"}
        onPress={() => setOpen(true)}
        styleType={hasUnresolved ? "primary" : "lightStroke"}
        textStyle={triggerTextStyle}
        useTranslationText={false}
        style={[styles.trigger, triggerStyle, { paddingHorizontal: hasUnresolved ? 8 : 12 }]}
        size="xs"
        leftIcon={
          <ChatCircleIcon
            size={15}
            weight={hasUnresolved || hasResolvedOnly ? "fill" : "regular"}
            color={triggerTextStyle.color}
          />
        }
        leftIconStyle={{ marginRight: 0 }}
        rightIcon={undefined}
      />

      <Modal
        visible={open}
        transparent
        animationType="fade"
        onRequestClose={() => setOpen(false)}
        statusBarTranslucent={Platform.OS === "android"}>
        <TouchableWithoutFeedback onPress={() => setOpen(false)}>
          <View style={styles.backdrop} />
        </TouchableWithoutFeedback>

        <SafeAreaView style={styles.safeArea} pointerEvents="box-none">
          <View style={styles.popoverPositioner} pointerEvents="box-none">
            <View style={[styles.popover, { width: popoverWidth }]}>
              {/* Header */}
              <View style={styles.popoverHeader}>
                <View style={styles.popoverHeaderLeft}>
                  <CustomText
                    text={contextLabel ?? "Comments"}
                    useTranslationText={false}
                    style={styles.popoverTitle}
                  />
                  {counts.total > 0 && (
                    <CustomText
                      text={`${counts.unresolved} open · ${counts.total - counts.unresolved} resolved`}
                      useTranslationText={false}
                      style={styles.popoverSubtitle}
                    />
                  )}
                </View>
              </View>

              <ScrollView
                style={styles.popoverBody}
                contentContainerStyle={styles.popoverBodyContent}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator={false}>
                <CommentsSection
                  targetKind={targetKind}
                  targetId={targetId}
                  api={api}
                  contextLabel={contextLabel}
                  disabled={disabled}
                />
              </ScrollView>
            </View>
          </View>
        </SafeAreaView>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  trigger: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    height: 32,
    borderRadius: 16,
    paddingHorizontal: 10,
  },
  triggerUnresolved: {
    backgroundColor: "#F3E8FF",
  },
  triggerResolved: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  triggerEmpty: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  triggerTextUnresolved: {
    color: "#7E22CE",
  },
  triggerTextResolved: {
    color: "#6B7280",
  },
  triggerTextEmpty: {
    color: "#475467",
  },

  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0, 0, 0, 0.25)",
  },

  safeArea: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },

  popoverPositioner: {
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 16,
  },

  popover: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.12,
    shadowRadius: 24,
    elevation: 12,
    overflow: "hidden",
  },

  popoverHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 12,
  },
  popoverHeaderLeft: {
    flex: 1,
    minWidth: 0,
  },
  popoverTitle: {
    fontSize: 14,
    fontWeight: "600",
    color: "#101828",
  },
  popoverSubtitle: {
    fontSize: 11,
    color: "#6B7280",
    marginTop: 2,
  },
  popoverBody: {
    flex: 1,
  },
  popoverBodyContent: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
});
